import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Users, BarChart3, TrendingUp, Clock, MousePointerClick,
  FileDown, Activity, RefreshCw, AlertCircle, ExternalLink, Search,
  CheckCircle, Eye, Globe,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import {
  fetchLeadPortalAnalytics,
  fetchLeadsPortalActivity,
} from '../services/leadPortalAnalyticsService';
import { initiateIntegrationConnection, isIntegrationConfigured } from '../services/integrationService';
import PortalBackground from '../components/PortalBackground';

// ---------------------------------------------------------------------------
// Date range presets → GA4 expression strings
// ---------------------------------------------------------------------------
const DATE_PRESETS = [
  { label: 'Last 7 days',   startDate: '7daysAgo',  endDate: 'today' },
  { label: 'Last 30 days',  startDate: '30daysAgo', endDate: 'today' },
  { label: 'Last 90 days',  startDate: '90daysAgo', endDate: 'today' },
  { label: 'Last 6 months', startDate: '180daysAgo', endDate: 'today' },
  { label: 'This year',     startDate: 'yearAgo',   endDate: 'today' },
];

// Colour palette for charts
const CHART_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

// Human-readable event names for display
const EVENT_LABELS = {
  scroll:           'Scroll depth',
  click:            'Outbound click',
  file_download:    'File download',
  form_start:       'Form started',
  form_submit:      'Form submitted',
  tab_click:        'Sidebar nav click',
  module_view:      'Module view',
  button_click:     'Button click',
  video_start:      'Video play',
  in_page_tab_click:'In-page tab click',
};

function fmt(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n));
}

function fmtDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(d);
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ icon, label, value, sub, darkMode }) {
  return (
    <div className={`rounded-2xl border p-4 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
        {icon}
      </div>
      <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ title, darkMode }) {
  return (
    <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
  );
}

function Panel({ children, darkMode, className = '' }) {
  return (
    <div className={`rounded-2xl border p-5 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function SetupBanner({ darkMode, onConnect, message, permissionError, connectedEmail }) {
  const isConnectedNoAccess = permissionError || (message && /connected as/i.test(message));
  const title = isConnectedNoAccess
    ? 'Google Analytics connected — property access needed'
    : 'Connect Google Analytics to load charts';
  const defaultMessage = isConnectedNoAccess
    ? `Add ${connectedEmail || 'the Google account you used'} in GA4 → Admin → Property access management for property 481707605 (Viewer or higher), then click Refresh. Or reconnect with a different Google account.`
    : 'Sign in with a Google account that has Viewer access on GA4 property 481707605. Browser tracking (Measurement ID) works separately — this connection powers the report charts.';

  return (
    <div className={`rounded-lg border-l-4 p-5 mb-6 ${isConnectedNoAccess ? 'border-blue-400' : 'border-amber-400'} ${darkMode ? (isConnectedNoAccess ? 'bg-blue-900/20' : 'bg-amber-900/20') : (isConnectedNoAccess ? 'bg-blue-50' : 'bg-amber-50')}`}>
      <div className="flex items-start gap-3">
        {isConnectedNoAccess
          ? <CheckCircle className="text-blue-500 mt-0.5 flex-shrink-0" size={20} />
          : <AlertCircle className="text-amber-500 mt-0.5 flex-shrink-0" size={20} />}
        <div className="flex-1">
          <p className={`font-semibold ${darkMode ? (isConnectedNoAccess ? 'text-blue-300' : 'text-amber-300') : (isConnectedNoAccess ? 'text-blue-800' : 'text-amber-800')}`}>
            {title}
          </p>
          {connectedEmail && isConnectedNoAccess && (
            <p className={`text-sm mt-1 font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Signed in as {connectedEmail}
            </p>
          )}
          <p className={`text-sm mt-1 ${darkMode ? (isConnectedNoAccess ? 'text-blue-400' : 'text-amber-400') : (isConnectedNoAccess ? 'text-blue-700' : 'text-amber-700')}`}>
            {message || defaultMessage}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            {onConnect && (
              <button
                type="button"
                onClick={onConnect}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition ${isConnectedNoAccess ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                <Globe size={14} />
                {isConnectedNoAccess ? 'Reconnect Google Analytics' : 'Connect Google Analytics'}
              </button>
            )}
            {isConnectedNoAccess && (
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                Open GA4 property access <ExternalLink size={12} />
              </a>
            )}
            {!isConnectedNoAccess && (
              <a
                href="https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com?project=bba-portal-894c2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-amber-600 hover:text-amber-800 font-medium"
              >
                Enable GA4 Data API <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const LeadPortalReports = () => {
  const { darkMode } = useTheme();

  const [datePreset, setDatePreset] = useState(1); // index into DATE_PRESETS
  const [ga4Data, setGa4Data] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(true);
  const [ga4Error, setGa4Error] = useState(null);

  const [leadsData, setLeadsData] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [showPortalOnly, setShowPortalOnly] = useState(false);

  const selectedPreset = DATE_PRESETS[datePreset];

  // Load GA4 data
  const loadGa4 = useCallback(async () => {
    setGa4Loading(true);
    setGa4Error(null);
    try {
      const data = await fetchLeadPortalAnalytics({
        startDate: selectedPreset.startDate,
        endDate: selectedPreset.endDate,
      });
      setGa4Data(data);
    } catch (err) {
      setGa4Error(err.message || 'Failed to load analytics');
    } finally {
      setGa4Loading(false);
    }
  }, [selectedPreset.startDate, selectedPreset.endDate]);

  // Load leads activity (once on mount)
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchLeadsPortalActivity();
        setLeadsData(data);
      } catch (err) {
        console.error('Error loading leads portal activity:', err);
      } finally {
        setLeadsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    loadGa4();
  }, [loadGa4]);

  const noGA4 = ga4Data?.configError;

  const handleConnectGa4 = () => {
    if (!isIntegrationConfigured('googleanalytics')) {
      setGa4Error('Google OAuth client is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env.');
      return;
    }
    initiateIntegrationConnection('googleanalytics');
  };

  // Prepare traffic sources for chart
  const sourcesChartData = useMemo(() => {
    if (!ga4Data?.sources) return [];
    const grouped = {};
    ga4Data.sources.forEach(row => {
      const key = row.source === '(direct)' ? 'Direct' : row.source || 'Unknown';
      grouped[key] = (grouped[key] || 0) + (row.sessions || 0);
    });
    return Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [ga4Data]);

  // Prepare events for chart
  const eventsChartData = useMemo(() => {
    if (!ga4Data?.events) return [];
    return ga4Data.events.map(row => ({
      name: EVENT_LABELS[row.eventName] || row.eventName,
      count: row.eventCount || 0,
    }));
  }, [ga4Data]);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    let list = leadsData;
    if (showPortalOnly) list = list.filter(l => l.portalUserId);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(l =>
        l.company?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      // Leads with portal users first, then by last login recency
      if (a.portalUserId && !b.portalUserId) return -1;
      if (!a.portalUserId && b.portalUserId) return 1;
      if (a.lastLogin && b.lastLogin) return new Date(b.lastLogin) - new Date(a.lastLogin);
      return 0;
    });
  }, [leadsData, search, showPortalOnly]);

  const leadsWithPortal = leadsData.filter(l => l.portalUserId).length;
  const leadsLoggedIn = leadsData.filter(l => l.lastLogin).length;

  return (
    <div className="min-h-screen p-6 relative text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">

        {/* Header */}
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 mb-8">
          <BackButton />
          <div className="text-center min-w-0 px-2">
            <h1 className={`text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>
              Lead Portal Analytics
            </h1>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Start portal traffic, engagement events, and per-lead activity
            </p>
          </div>
          <button
            onClick={loadGa4}
            disabled={ga4Loading}
            className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border transition shrink-0 ${darkMode ? 'border-gray-600 hover:bg-gray-800 text-gray-300' : 'border-gray-300 hover:bg-gray-100 text-gray-700'}`}
          >
            <RefreshCw size={14} className={ga4Loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Setup Banner */}
        {noGA4 && !ga4Loading && (
          <SetupBanner
            darkMode={darkMode}
            onConnect={handleConnectGa4}
            message={ga4Data?.message}
            permissionError={ga4Data?.permissionError}
            connectedEmail={ga4Data?.connectedEmail}
          />
        )}

        {/* Date filter */}
        <Panel darkMode={darkMode} className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date range:</span>
            <div className="flex gap-2 flex-wrap">
              {DATE_PRESETS.map((p, i) => (
                <button
                  key={p.label}
                  onClick={() => setDatePreset(i)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition ${
                    datePreset === i
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : darkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </Panel>

        {/* GA4 error */}
        {ga4Error && !noGA4 && (
          <div className={`rounded-lg border-l-4 border-red-500 p-4 mb-6 text-sm ${darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}`}>
            <strong>Analytics error:</strong> {ga4Error}
          </div>
        )}

        {/* ── Overview KPIs ── */}
        <div className="mb-8">
          <SectionHeader title="Start Portal Overview" darkMode={darkMode} />
          {ga4Loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className={`rounded-2xl border p-4 animate-pulse h-24 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm`} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
              <KpiCard icon={<Users size={16} className="text-emerald-500" />} label="Active users" value={fmt(ga4Data?.overview?.activeUsers)} darkMode={darkMode} />
              <KpiCard icon={<Users size={16} className="text-blue-500" />} label="New users" value={fmt(ga4Data?.overview?.newUsers)} darkMode={darkMode} />
              <KpiCard icon={<Activity size={16} className="text-violet-500" />} label="Sessions" value={fmt(ga4Data?.overview?.sessions)} darkMode={darkMode} />
              <KpiCard icon={<Eye size={16} className="text-sky-500" />} label="Page views" value={fmt(ga4Data?.overview?.pageViews)} darkMode={darkMode} />
              <KpiCard icon={<Clock size={16} className="text-amber-500" />} label="Avg session" value={fmtDuration(ga4Data?.overview?.avgSessionDuration)} darkMode={darkMode} />
              <KpiCard icon={<TrendingUp size={16} className="text-green-500" />} label="Engagement" value={fmtPct(ga4Data?.overview?.engagementRate)} darkMode={darkMode} />
              <KpiCard icon={<MousePointerClick size={16} className="text-red-400" />} label="Bounce rate" value={fmtPct(ga4Data?.overview?.bounceRate)} darkMode={darkMode} />
            </div>
          )}
        </div>

        {/* ── Traffic sources + Engagement side by side ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

          {/* Traffic Sources */}
          <Panel darkMode={darkMode}>
            <SectionHeader title="Traffic Sources" darkMode={darkMode} />
            {ga4Loading ? (
              <div className={`animate-pulse h-64 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
            ) : sourcesChartData.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No source data yet.</p>
            ) : (
              <div className="flex flex-col gap-4">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={sourcesChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {sourcesChartData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v} sessions`, 'Sessions']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                        <th className="text-left py-1 font-medium">Source</th>
                        <th className="text-right py-1 font-medium">Sessions</th>
                        <th className="text-right py-1 font-medium">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ga4Data?.sources || []).slice(0, 8).map((row, i) => (
                        <tr key={i} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                          <td className="py-1.5">
                            <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{row.source || '(direct)'}</span>
                            {row.medium && row.medium !== '(none)' && (
                              <span className={`ml-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>/ {row.medium}</span>
                            )}
                          </td>
                          <td className="text-right py-1.5">{fmt(row.sessions)}</td>
                          <td className="text-right py-1.5">{fmt(row.activeUsers)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Panel>

          {/* Engagement Events */}
          <Panel darkMode={darkMode}>
            <SectionHeader title="Engagement Events" darkMode={darkMode} />
            {ga4Loading ? (
              <div className={`animate-pulse h-64 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
            ) : eventsChartData.length === 0 ? (
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No event data yet — tracking will populate once users interact with the Start portal.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={eventsChartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} />
                  <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: darkMode ? '#d1d5db' : '#374151' }} />
                  <Tooltip
                    contentStyle={{ background: darkMode ? '#1f2937' : '#fff', border: '1px solid #374151', borderRadius: 6 }}
                    labelStyle={{ color: darkMode ? '#f3f4f6' : '#111827' }}
                  />
                  <Bar dataKey="count" name="Events" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        {/* ── Top Start Pages ── */}
        <Panel darkMode={darkMode} className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <SectionHeader title="Top Start Pages" darkMode={darkMode} />
            <Globe size={16} className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
          </div>
          {ga4Loading ? (
            <div className={`animate-pulse h-48 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
          ) : !ga4Data?.pages?.length ? (
            <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No page data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                    <th className="text-left py-2 font-medium">Page path</th>
                    <th className="text-left py-2 font-medium hidden sm:table-cell">Title</th>
                    <th className="text-right py-2 font-medium">Views</th>
                    <th className="text-right py-2 font-medium">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {ga4Data.pages.map((row, i) => (
                    <tr key={i} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className={`py-2 font-mono text-xs ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{row.pagePath}</td>
                      <td className={`py-2 hidden sm:table-cell ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{row.pageTitle || '—'}</td>
                      <td className="py-2 text-right">{fmt(row.pageViews)}</td>
                      <td className="py-2 text-right">{fmt(row.activeUsers)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* ── Leads Activity ── */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Leads Portal Activity</h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {leadsWithPortal} of {leadsData.length} leads have signed up for the Start portal · {leadsLoggedIn} have logged in
              </p>
            </div>
          </div>

          {/* Leads summary KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <KpiCard icon={<Users size={16} className="text-blue-500" />} label="Total leads" value={fmt(leadsData.length)} darkMode={darkMode} />
            <KpiCard icon={<CheckCircle size={16} className="text-emerald-500" />} label="Portal signups" value={fmt(leadsWithPortal)} sub={leadsData.length ? `${Math.round(leadsWithPortal / leadsData.length * 100)}% of leads` : undefined} darkMode={darkMode} />
            <KpiCard icon={<Activity size={16} className="text-violet-500" />} label="Logged in" value={fmt(leadsLoggedIn)} darkMode={darkMode} />
            <KpiCard icon={<FileDown size={16} className="text-amber-500" />} label="Free resource claimed" value={fmt(leadsData.filter(l => l.freeResourceClaimed).length)} darkMode={darkMode} />
          </div>

          {/* Search / filter bar */}
          <Panel darkMode={darkMode} className="mb-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className={`flex items-center gap-2 flex-1 min-w-[200px] rounded border px-3 py-1.5 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                <Search size={14} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                <input
                  type="text"
                  placeholder="Search by company or email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={`flex-1 text-sm bg-transparent outline-none ${darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                />
              </div>
              <label className={`flex items-center gap-2 text-sm cursor-pointer select-none ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <input
                  type="checkbox"
                  checked={showPortalOnly}
                  onChange={e => setShowPortalOnly(e.target.checked)}
                  className="rounded"
                />
                Portal signups only
              </label>
              {leadsLoading && <RefreshCw size={14} className="animate-spin text-gray-400" />}
            </div>
          </Panel>

          {/* Leads table */}
          <Panel darkMode={darkMode}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                    <th className="text-left py-2 font-medium">Company / Lead</th>
                    <th className="text-left py-2 font-medium hidden md:table-cell">Email</th>
                    <th className="text-left py-2 font-medium">Portal signup</th>
                    <th className="text-left py-2 font-medium hidden sm:table-cell">Last login</th>
                    <th className="text-center py-2 font-medium hidden lg:table-cell">Logins</th>
                    <th className="text-center py-2 font-medium hidden lg:table-cell">Step</th>
                    <th className="text-center py-2 font-medium">Resource</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`py-8 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {leadsLoading ? 'Loading…' : 'No leads found.'}
                      </td>
                    </tr>
                  ) : filteredLeads.map(lead => (
                    <tr key={lead.accountId} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className="py-2.5">
                        <a
                          href={`/crm/lead/${lead.accountId}`}
                          className={`font-medium hover:underline ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}
                        >
                          {lead.company || '(unnamed)'}
                        </a>
                      </td>
                      <td className={`py-2.5 hidden md:table-cell ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {lead.email || '—'}
                      </td>
                      <td className="py-2.5">
                        {lead.portalUserId ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400`}>
                            {fmtDate(lead.portalSignupDate) !== '—' ? fmtDate(lead.portalSignupDate) : 'Signed up'}
                          </span>
                        ) : (
                          <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Not signed up</span>
                        )}
                      </td>
                      <td className={`py-2.5 hidden sm:table-cell text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {fmtDate(lead.lastLogin)}
                      </td>
                      <td className={`py-2.5 hidden lg:table-cell text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {lead.loginCount > 0 ? lead.loginCount : '—'}
                      </td>
                      <td className={`py-2.5 hidden lg:table-cell text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {lead.startFlowStep != null ? lead.startFlowStep : '—'}
                      </td>
                      <td className="py-2.5 text-center">
                        {lead.freeResourceClaimed ? (
                          <CheckCircle size={14} className="text-emerald-500 mx-auto" />
                        ) : (
                          <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredLeads.length > 0 && (
              <p className={`text-xs mt-3 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                Showing {filteredLeads.length} of {leadsData.length} leads
              </p>
            )}
          </Panel>
        </div>

      </div>
    </div>
  );
};

export default LeadPortalReports;
